import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Beer, MapPin, ArrowRight } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { cities, getCityBySlug } from "@/data/cities";

const CityPage = () => {
  const { city: slug } = useParams<{ city: string }>();
  const city = slug ? getCityBySlug(slug) : undefined;
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "de").split("-")[0];

  if (!city) return <Navigate to="/" replace />;

  const localizedName = lang === "de" && city.nameDe ? city.nameDe : city.name;
  const plannerHref = `/?city=${encodeURIComponent(localizedName)}#planner`;

  const canonical = `https://goldenmile.bar/pub-crawl/${city.slug}`;
  const title = city.titleOverride ?? `${city.name} Pub Crawl Map & Route Planner | Golden Mile`;
  const description = city.descriptionOverride ?? `Plan a pub crawl in ${city.name}${city.nameDe && city.nameDe !== city.name ? ` (${city.nameDe})` : ""}. Choose a neighborhood, create a walking map with venue hours and a schedule, and share via Google Maps.`;

  const faqs = [
    {
      q: `Where can I start a pub crawl in ${city.name}?`,
      a: `Try a starting point in ${city.neighborhoods.slice(0, 3).join(", ")}. Search one neighborhood or a specific pub rather than the whole city, then compare the suggested stops and walking times. Results depend on nearby venues and available opening hours.`,
    },
    {
      q: `How many bars should a ${city.name} pub crawl include?`,
      a: `The planner accepts 3–10 stops. Four stops allow about two hours at venues, and six allow about three hours, based on 30 minutes per stop. Add the walking time shown in your generated schedule to estimate the total duration.`,
    },
    {
      q: `Is the ${city.name} pub crawl planner free?`,
      a: `Yes. Generating a pub crawl or bar crawl route in ${city.name} with Golden Mile is completely free — no signup, no app download.`,
    },
    {
      q: `Can I share my ${city.name} pub crawl map on Google Maps?`,
      a: "Yes. Choose Google Maps link on your selected route to copy and open walking directions. The current location is the navigation start and the final bar is the destination. Google Maps may limit intermediate stops, especially on mobile; check the route before sharing it.",
    },
    {
      q: `Is this a guided ${city.name} pub crawl?`,
      a: "No. Golden Mile creates a self-guided map and schedule. It does not book venues or include a guide, drinks or admission. Confirm group entry and changed opening hours directly with the venues.",
    },
  ];

  const schemaJson = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: canonical,
      isPartOf: { "@id": "https://goldenmile.bar/#website" },
      inLanguage: "en",
      about: {
        "@type": "City",
        name: city.name,
        address: { "@type": "PostalAddress", addressCountry: city.country },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://goldenmile.bar/" },
        { "@type": "ListItem", position: 2, name: `Pub crawl ${city.name}`, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const otherCities = cities.filter((c) => c.slug !== city.slug);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={title} description={description} canonical={canonical} schemaJson={schemaJson} />

      <header className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5" />
        <div className="absolute right-3 top-3 z-10 sm:right-4 sm:top-4">
          <LanguageSwitcher />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 py-12 text-center sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">{t("hero.title_highlight")}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Pub crawl {city.name}</span>
          </nav>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {city.name}, {city.country}
          </div>
          <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-5xl">
            Pub Crawl <span className="text-primary">{city.name}</span>
            {city.nameDe && city.nameDe !== city.name && (
              <span className="mt-1 block text-lg font-medium text-muted-foreground sm:text-xl">
                Kneipentour {city.nameDe}
              </span>
            )}
          </h1>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Create a {city.name} pub crawl map with walking times and a per-stop schedule. Choose a neighborhood or starting pub, then share your selected bar crawl route with a Google Maps link.
          </p>
          <Button asChild className="mt-6 h-auto whitespace-normal px-6 py-3">
          <Link to={plannerHref}>
            Plan my {city.name} pub crawl <ArrowRight className="h-4 w-4" />
          </Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12">
        <section className="space-y-4 text-muted-foreground">
          <p>{city.intro}</p>
          <p>
            Start from your current location, a specific bar or a neighborhood such as {city.neighborhoods.slice(0, 3).join(", ")}. Golden Mile suggests walking routes with 3–10 stops using nearby venues and available Google opening hours. Check with venues for any last-minute changes.
          </p>
          <p>
            Use the map for a birthday, an after-work round or an evening with friends. This is a self-guided planner, not a booking service: confirm reservations and group entry with the venues before you go.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="mb-4 text-2xl font-bold">Neighborhood starting points in {city.name}</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {city.neighborhoods.map((n) => (
              <li
                key={n}
                className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/50 p-4"
              >
                <Beer className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="font-medium">{n}</span>
              </li>
            ))}
          </ul>
        </section>

        {city.routeAdvice && (
          <section className="mt-12 space-y-6" aria-label={`${city.name} route planning notes`}>
            {city.routeAdvice.map((advice) => (
              <article key={advice.heading}>
                <h2 className="mb-3 text-2xl font-bold">{advice.heading}</h2>
                <p className="text-muted-foreground">{advice.text}</p>
              </article>
            ))}
          </section>
        )}

        <section className="mt-12">
          <h2 className="mb-4 text-2xl font-bold">How to plan a pub crawl in {city.name}</h2>
          <ol className="list-decimal space-y-3 pl-5 text-muted-foreground">
            <li>Pick a starting point in {city.name} — your current location, a specific bar, or a neighborhood like {city.neighborhoods[0]}.</li>
            <li>Choose how many stops you want (3–10). For a {city.name} pub crawl, 4–6 bars is the sweet spot.</li>
            <li>Compare the suggested walking routes, available opening hours and schedules. Open the Google Maps link on your chosen route to share it.</li>
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="mb-6 text-2xl font-bold">Pub crawl {city.name} — frequently asked questions</h2>
          <div className="space-y-6">
            {faqs.map((f) => (
              <article key={f.q}>
                <h3 className="mb-2 font-semibold">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12 text-center">
          <Button asChild className="h-auto whitespace-normal px-6 py-3">
          <Link to={plannerHref}>
            Generate my {city.name} pub crawl now <ArrowRight className="h-4 w-4" />
          </Link>
          </Button>
        </section>

        <section className="mt-16 border-t border-border pt-10">
          <h2 className="mb-4 text-xl font-bold">Pub crawls in other cities</h2>
          <ul className="flex flex-wrap gap-2 text-sm">
            {otherCities.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/pub-crawl/${c.slug}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-card/50 px-3 py-1.5 text-muted-foreground hover:border-primary/40 hover:text-primary"
                >
                  Pub crawl {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className="border-t border-border bg-card/30 px-4 py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Golden Mile. {t("seo.footer_rights")}
      </footer>
    </div>
  );
};

export default CityPage;
